import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Link,
  Card,
  CardContent,
} from '@mui/material';
import {
  Email,
  Phone,
  Person,
  Work,
  School,
  Description,
  CalendarToday,
  LocationOn,
  Visibility,
  Download,
} from '@mui/icons-material';
import { Doctor } from '../../types';
import { doctorService } from '../../services/doctorService';
import { getApiUrl } from '../../config/api';

interface DoctorViewDialogProps {
  open: boolean;
  onClose: () => void;
  doctor: Doctor | null;
}

const DoctorViewDialog: React.FC<DoctorViewDialogProps> = ({ open, onClose, doctor }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doctorDetails, setDoctorDetails] = useState<any>(null);

  useEffect(() => {
    if (open && doctor) {
      fetchDoctorDetails();
    }
  }, [open, doctor]);

  const fetchDoctorDetails = async () => {
    if (!doctor) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch full doctor details from admin API
      const token = localStorage.getItem('admin_token');
      const response = await fetch(getApiUrl(`/admin/doctors/${doctor.id}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch doctor details');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setDoctorDetails(data.data);
      } else {
        setDoctorDetails(doctor);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch doctor details');
      setDoctorDetails(doctor);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = (documentUrl: string) => {
    window.open(documentUrl, '_blank');
  };

  if (!doctor) return null;

  const displayDoctor = doctorDetails || doctor;
  const documents = displayDoctor.documents || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            src={displayDoctor.profileImage}
            sx={{ width: 56, height: 56, bgcolor: '#00A76F' }}
          >
            {displayDoctor.fullName?.split(' ').map((n: string) => n[0]).join('') || 'D'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {displayDoctor.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayDoctor.specialization || 'Doctor'}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Basic Information */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {displayDoctor.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {displayDoctor.phoneNumber}
                  </Typography>
                </Grid>
                {displayDoctor.dateOfBirth && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(displayDoctor.dateOfBirth).toLocaleDateString()}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.gender && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Gender</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.gender.charAt(0).toUpperCase() + displayDoctor.gender.slice(1)}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.address && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Address</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.address}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Professional Information */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Professional Information
              </Typography>
              <Grid container spacing={2}>
                {displayDoctor.doctorId && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Work fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Doctor ID</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.doctorId}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.department && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Work fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Department</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.department.name}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.experience && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Work fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Experience</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.experience}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.qualifications && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <School fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">Qualifications</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.qualifications}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.licenseNumber && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Description fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">License Number</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.licenseNumber}
                    </Typography>
                  </Grid>
                )}
                {displayDoctor.aboutYourself && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">About</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {displayDoctor.aboutYourself}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Documents */}
            {documents.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Documents
                </Typography>
                <Grid container spacing={2}>
                  {documents.map((doc: string, index: number) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Description color="primary" />
                            <Box flex={1}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Document {index + 1}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {doc.split('/').pop() || `Document ${index + 1}`}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={1}>
                              <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleDownloadDocument(doc)}
                                variant="outlined"
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                startIcon={<Download />}
                                onClick={() => handleDownloadDocument(doc)}
                                variant="outlined"
                              >
                                Download
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* Status Information */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Status Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                  </Box>
                  <Chip
                    label={displayDoctor.isActive ? 'Active' : 'Inactive'}
                    color={displayDoctor.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" color="text.secondary">Approval Status</Typography>
                  </Box>
                  <Chip
                    label={displayDoctor.isApproved ? 'Approved' : 'Pending'}
                    color={displayDoctor.isApproved ? 'success' : 'warning'}
                    size="small"
                  />
                </Grid>
                {displayDoctor.approvedAt && (
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">Approved At</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(displayDoctor.approvedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(displayDoctor.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DoctorViewDialog;

